import React, { useState, useEffect } from 'react';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import MetricCard from './components/MetricCard';
import PerformanceChart from './components/PerformanceChart';
import TechnicianPerformanceTable from './components/TechnicianPerformanceTable';
import FilterPanel from './components/FilterPanel';
import RealtimeMonitor from './components/RealtimeMonitor';
import GeographicDistribution from './components/GeographicDistribution';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const AnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('last30days');

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleLogout = () => {
    console.log('Déconnexion');
  };

  const handleApplyFilters = (filters) => {
    console.log('Filtres appliqués:', filters);
    setDateRange(filters?.dateRange);
  };

  const handleExportReport = () => {
    console.log('Export du rapport');
  };

  const kpiMetrics = [
  {
    title: 'Taux de conversion',
    value: '68,5%',
    change: '+5,2%',
    changeType: 'positive',
    icon: 'TrendingUp',
    trend: [45, 52, 48, 61, 55, 68, 72, 68]
  },
  {
    title: 'Taux de no-show',
    value: '4,2%',
    change: '-1,8%',
    changeType: 'positive',
    icon: 'UserX',
    trend: [8, 7, 6, 5, 6, 5, 4, 4]
  },
  {
    title: 'Temps moyen intervention',
    value: '2h 15min',
    change: '-12min',
    changeType: 'positive',
    icon: 'Clock',
    trend: [150, 145, 140, 138, 135, 135, 132, 135]
  },
  {
    title: 'Résolution 1er passage',
    value: '89,3%',
    change: '+3,1%',
    changeType: 'positive',
    icon: 'CheckCircle',
    trend: [82, 84, 85, 86, 87, 88, 89, 89]
  },
  {
    title: 'Précision ETA',
    value: '94,7%',
    change: '+2,4%',
    changeType: 'positive',
    icon: 'Target',
    trend: [88, 90, 91, 92, 93, 94, 95, 95]
  },
  {
    title: 'Respect des RDV',
    value: '96,1%',
    change: '+1,3%',
    changeType: 'positive',
    icon: 'Calendar',
    trend: [93, 94, 94, 95, 95, 96, 96, 96]
  },
  {
    title: 'NPS Score',
    value: '72',
    change: '+8 points',
    changeType: 'positive',
    icon: 'Heart',
    trend: [58, 62, 65, 67, 68, 70, 71, 72]
  },
  {
    title: 'Satisfaction client',
    value: '4,6/5',
    change: '+0,2',
    changeType: 'positive',
    icon: 'Star',
    trend: [4.2, 4.3, 4.3, 4.4, 4.5, 4.5, 4.6, 4.6]
  }];


  const revenueChartData = [
  { name: 'Lun', revenus: 12500, interventions: 45 },
  { name: 'Mar', revenus: 15200, interventions: 52 },
  { name: 'Mer', revenus: 14800, interventions: 48 },
  { name: 'Jeu', revenus: 18900, interventions: 61 },
  { name: 'Ven', revenus: 16700, interventions: 55 },
  { name: 'Sam', revenus: 21300, interventions: 68 },
  { name: 'Dim', revenus: 19800, interventions: 62 }];


  const performanceChartData = [
  { name: 'Jan', completionRate: 85, satisfaction: 4.3, etaAccuracy: 91 },
  { name: 'Fév', completionRate: 87, satisfaction: 4.4, etaAccuracy: 92 },
  { name: 'Mar', completionRate: 86, satisfaction: 4.3, etaAccuracy: 93 },
  { name: 'Avr', completionRate: 88, satisfaction: 4.5, etaAccuracy: 94 },
  { name: 'Mai', completionRate: 89, satisfaction: 4.5, etaAccuracy: 94 },
  { name: 'Juin', completionRate: 90, satisfaction: 4.6, etaAccuracy: 95 }];


  const technicianData = [
  {
    id: 1,
    name: 'Jean Dupont',
    specialty: 'Plomberie générale',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_199d46426-1763293959454.png",
    avatarAlt: 'Portrait professionnel de Jean Dupont, technicien plombier avec cheveux courts bruns et uniforme bleu',
    interventions: 156,
    completionRate: 94,
    avgTime: '2h 05min',
    satisfaction: 4.8,
    status: 'active'
  },
  {
    id: 2,
    name: 'Marie Laurent',
    specialty: 'Chauffage',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1377d2e1d-1763295857859.png",
    avatarAlt: 'Portrait professionnel de Marie Laurent, technicienne chauffagiste avec cheveux blonds attachés et uniforme bleu',
    interventions: 142,
    completionRate: 92,
    avgTime: '2h 15min',
    satisfaction: 4.7,
    status: 'busy'
  },
  {
    id: 3,
    name: 'Pierre Martin',
    specialty: 'Urgences',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_128d12577-1765972516581.png",
    avatarAlt: 'Portrait professionnel de Pierre Martin, technicien urgences avec barbe courte et uniforme bleu',
    interventions: 189,
    completionRate: 91,
    avgTime: '1h 45min',
    satisfaction: 4.6,
    status: 'active'
  },
  {
    id: 4,
    name: 'Sophie Bernard',
    specialty: 'Maintenance',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1c7be1399-1763300910183.png",
    avatarAlt: 'Portrait professionnel de Sophie Bernard, technicienne maintenance avec cheveux châtains courts et uniforme bleu',
    interventions: 134,
    completionRate: 96,
    avgTime: '2h 30min',
    satisfaction: 4.9,
    status: 'active'
  },
  {
    id: 5,
    name: 'Luc Petit',
    specialty: 'Plomberie générale',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_12e5f75e8-1763293773539.png",
    avatarAlt: 'Portrait professionnel de Luc Petit, technicien plombier avec lunettes et cheveux gris courts en uniforme bleu',
    interventions: 128,
    completionRate: 88,
    avgTime: '2h 20min',
    satisfaction: 4.5,
    status: 'offline'
  }];


  const realtimeData = {
    lastUpdate: '2 min',
    metrics: [
    {
      id: 1,
      label: 'Interventions actives',
      description: 'En cours actuellement',
      value: '24',
      change: '+3',
      changeType: 'positive',
      status: 'active'
    },
    {
      id: 2,
      label: 'Utilisation techniciens',
      description: 'Taux d\'occupation',
      value: '87%',
      change: '+5%',
      changeType: 'positive',
      status: 'warning'
    },
    {
      id: 3,
      label: 'File d\'attente support',
      description: 'Demandes en attente',
      value: '12',
      change: '-4',
      changeType: 'positive',
      status: 'active'
    }]

  };

  const geographicData = [
  {
    id: 1,
    name: 'Paris Centre',
    interventions: 342,
    percentage: 28,
    revenue: '89 450 €',
    satisfaction: 4.7,
    technicians: 12
  },
  {
    id: 2,
    name: 'Île-de-France Nord',
    interventions: 298,
    percentage: 24,
    revenue: '76 200 €',
    satisfaction: 4.6,
    technicians: 10
  },
  {
    id: 3,
    name: 'Île-de-France Sud',
    interventions: 267,
    percentage: 22,
    revenue: '68 900 €',
    satisfaction: 4.5,
    technicians: 9
  },
  {
    id: 4,
    name: 'Banlieue Est',
    interventions: 189,
    percentage: 15,
    revenue: '45 300 €',
    satisfaction: 4.4,
    technicians: 6
  },
  {
    id: 5,
    name: 'Banlieue Ouest',
    interventions: 134,
    percentage: 11,
    revenue: '32 100 €',
    satisfaction: 4.3,
    technicians: 5
  }];


  const savedPresets = [
  {
    id: 1,
    name: 'Rapport mensuel',
    filters: {
      dateRange: 'thisMonth',
      serviceType: 'all',
      technicianTeam: 'all',
      region: 'all'
    }
  },
  {
    id: 2,
    name: 'Performance urgences',
    filters: {
      dateRange: 'last7days',
      serviceType: 'emergency',
      technicianTeam: 'all',
      region: 'all'
    }
  },
  {
    id: 3,
    name: 'Analyse Paris',
    filters: {
      dateRange: 'last30days',
      serviceType: 'all',
      technicianTeam: 'all',
      region: 'paris'
    }
  }];


  const tabs = [
  { id: 'overview', label: 'Vue d\'ensemble', icon: 'LayoutDashboard' },
  { id: 'performance', label: 'Performance', icon: 'TrendingUp' },
  { id: 'geographic', label: 'Géographie', icon: 'Map' },
  { id: 'realtime', label: 'Temps réel', icon: 'Activity' }];


  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader
        userRole="admin"
        userName="Admin PlumberPro"
        onLogout={handleLogout} />

      <main className="main-content">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-8">
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-3xl font-semibold text-foreground mb-2">
                  Tableau de bord analytique
                </h1>
                <p className="text-muted-foreground">
                  Surveillance des performances et indicateurs clés
                </p>
              </div>

              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  iconName="Download"
                  onClick={handleExportReport}>

                  Exporter le rapport
                </Button>
                <Button
                  variant="default"
                  iconName="Settings">

                  Configurer
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-thin">
              {tabs?.map((tab) =>
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 whitespace-nowrap ${
                activeTab === tab?.id ?
                'bg-primary text-primary-foreground shadow-md' :
                'bg-card text-muted-foreground hover:bg-muted hover:text-foreground border border-border'}`
                }>

                  <Icon name={tab?.icon} size={18} />
                  <span>{tab?.label}</span>
                </button>
              )}
            </div>
          </div>

          <FilterPanel
            onApplyFilters={handleApplyFilters}
            savedPresets={savedPresets} />


          {activeTab === 'overview' &&
          <div className="space-y-6 mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {kpiMetrics?.map((metric, index) =>
              <MetricCard
                key={index}
                title={metric?.title}
                value={metric?.value}
                change={metric?.change}
                changeType={metric?.changeType}
                icon={metric?.icon}
                trend={metric?.trend}
                loading={loading} />

              )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <PerformanceChart
                title="Revenus et interventions hebdomadaires"
                data={revenueChartData}
                type="bar"
                dataKeys={[
                { key: 'revenus', name: 'Revenus (€)', color: '#2563EB' },
                { key: 'interventions', name: 'Interventions', color: '#10B981' }]
                }
                loading={loading} />


                <PerformanceChart
                title="Évolution des indicateurs de performance"
                data={performanceChartData}
                type="line"
                dataKeys={[
                { key: 'completionRate', name: 'Taux de résolution (%)', color: '#2563EB' },
                { key: 'satisfaction', name: 'Satisfaction (x20)', color: '#F59E0B' },
                { key: 'etaAccuracy', name: 'Précision ETA (%)', color: '#10B981' }]
                }
                loading={loading} />

              </div>

              <TechnicianPerformanceTable
              data={technicianData}
              loading={loading} />

            </div>
          }

          {activeTab === 'performance' &&
          <div className="space-y-6 mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {kpiMetrics?.slice(0, 6)?.map((metric, index) =>
              <MetricCard
                key={index}
                title={metric?.title}
                value={metric?.value}
                change={metric?.change}
                changeType={metric?.changeType}
                icon={metric?.icon}
                trend={metric?.trend}
                loading={loading} />

              )}
              </div>

              <TechnicianPerformanceTable
              data={technicianData}
              loading={loading} />


              <PerformanceChart
              title="Analyse détaillée des performances mensuelles"
              data={performanceChartData}
              type="line"
              height={400}
              dataKeys={[
              { key: 'completionRate', name: 'Taux de résolution (%)', color: '#2563EB' },
              { key: 'satisfaction', name: 'Satisfaction (x20)', color: '#F59E0B' },
              { key: 'etaAccuracy', name: 'Précision ETA (%)', color: '#10B981' }]
              }
              loading={loading} />

            </div>
          }

          {activeTab === 'geographic' &&
          <div className="space-y-6 mt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <MetricCard
                title="Régions couvertes"
                value="5"
                icon="MapPin"
                loading={loading}
                change=""
                changeType="neutral" />

                <MetricCard
                title="Interventions totales"
                value="1 230"
                change="+12,5%"
                changeType="positive"
                icon="Activity"
                loading={loading} />

                <MetricCard
                title="Revenus totaux"
                value="311 950 €"
                change="+8,3%"
                changeType="positive"
                icon="Euro"
                loading={loading} />

              </div>

              <GeographicDistribution
              data={geographicData}
              loading={loading} />

            </div>
          }

          {activeTab === 'realtime' &&
          <div className="space-y-6 mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <RealtimeMonitor
                  data={realtimeData}
                  loading={loading} />

                </div>

                <div className="space-y-4">
                  <MetricCard
                  title="Techniciens actifs"
                  value="32/42"
                  icon="Users"
                  loading={loading}
                  change=""
                  changeType="neutral" />

                  <MetricCard
                  title="Temps de réponse moyen"
                  value="8 min"
                  change="-2 min"
                  changeType="positive"
                  icon="Clock"
                  loading={loading} />

                  <MetricCard
                  title="Taux de disponibilité"
                  value="98,5%"
                  icon="CheckCircle"
                  loading={loading}
                  change=""
                  changeType="neutral" />

                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <PerformanceChart
                title="Activité en temps réel (dernières 24h)"
                data={revenueChartData}
                type="line"
                dataKeys={[
                { key: 'interventions', name: 'Interventions', color: '#2563EB' }]
                }
                loading={loading} />


                <TechnicianPerformanceTable
                data={technicianData?.slice(0, 3)}
                loading={loading} />

              </div>
            </div>
          }
        </div>
      </main>
      <QuickActionToolbar
        userRole="admin"
        onEmergencyCall={() => console.log('Urgence')}
        onQuickBooking={() => console.log('Réservation rapide')}
        onSupportChat={() => console.log('Support')} />

    </div>);

};

export default AnalyticsDashboard;